import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CircularProgress,
    Alert,
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

// Importa el hook de empleados que ya tienes
import { useEmployees } from "../../../hooks/useEmployees"; // Asegúrate de que esta ruta sea correcta

// Esquema de validación con Yup
const assignEmployeeSchema = yup.object().shape({
    empid: yup.number()
        .required("El empleado es obligatorio.")
        .positive("Seleccione un empleado válido."),
    rolman: yup.string()
        .nullable()
        .max(50, "El rol no puede exceder 50 caracteres."),
});

const AssignEmployeeForm = ({ open, handleClose, onSubmit, maintenanceId }) => {
    // Obtener la lista de empleados disponibles
    const { employees, isLoading: isLoadingEmployees, error: errorEmployees } = useEmployees();

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(assignEmployeeSchema),
        defaultValues: {
            empid: null, // ID del empleado seleccionado
            rolman: "",  // Rol en el mantenimiento
        },
    });

    // Resetear el formulario cuando se abre o el maintenanceId cambia
    useEffect(() => {
        reset({
            empid: null,
            rolman: "",
        });
    }, [open, maintenanceId, reset]); // Asegúrate de resetear cuando 'open' cambia

    const handleFormSubmit = (data) => {
        // 'data' contendrá { empid, rolman }. El 'maintenanceId' se pasa desde el padre.
        onSubmit(data);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Asignar Empleado a Mantenimiento</DialogTitle>
            <DialogContent dividers>
                <Box component="form" noValidate sx={{ mt: 1 }}>
                    {/* Información del Mantenimiento (solo lectura para contexto) */}
                    <TextField
                        margin="dense"
                        label="Mantenimiento ID"
                        value={maintenanceId || 'N/A'}
                        fullWidth
                        variant="outlined"
                        disabled
                        sx={{ mb: 2 }}
                    />

                    {/* Selector de Empleado */}
                    {isLoadingEmployees ? (
                        <CircularProgress size={24} sx={{ mb: 2 }} />
                    ) : errorEmployees ? (
                        <Alert severity="error" sx={{ mb: 2 }}>Error al cargar empleados: {errorEmployees.message}</Alert>
                    ) : (
                        <Controller
                            name="empid"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth margin="dense" error={!!errors.empid} sx={{ mb: 2 }}>
                                    <InputLabel id="employee-select-label">Empleado</InputLabel>
                                    <Select
                                        labelId="employee-select-label"
                                        label="Empleado"
                                        {...field}
                                        value={field.value || ""} // Asegura que el valor sea "" para evitar warning si es null
                                    >
                                        <MenuItem value=""><em>Seleccione un empleado</em></MenuItem>
                                        {employees.map((emp) => (
                                            <MenuItem key={emp.empid} value={emp.empid}>
                                                {`${emp.pernom} ${emp.perape}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.empid && <FormHelperText>{errors.empid.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    )}

                    {/* Campo Rol en el Mantenimiento */}
                    <Controller
                        name="rolman"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="dense"
                                label="Rol en el Mantenimiento (Opcional)"
                                type="text"
                                fullWidth
                                variant="outlined"
                                error={!!errors.rolman}
                                helperText={errors.rolman?.message}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit">
                    Cancelar
                </Button>
                <Button onClick={handleSubmit(handleFormSubmit)} variant="contained" color="primary">
                    Asignar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignEmployeeForm;